import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Tabs, Tab, Spinner, Button, Pagination } from 'react-bootstrap';
import { AdminModal } from '@project/components';
import axios from '../../utils/axios';
import { RootContext } from '../../hoc/RootContext/RootContext';
import { buildAssetFieldLabelMap, formatChange, formatDateTime, parsePhotoUrls } from './historyUtils';
import { PhotoEyeButton, PhotoChangeCell, PhotoViewerModal } from './PhotoViewerModal';
import FieldChangesTab from './FieldChangesTab';
import AssetChangesTab from './AssetChangesTab';

// Re-export named symbols so existing test imports remain unchanged
export { parsePhotoUrls, formatChange, formatDateTime, buildAssetFieldLabelMap, PhotoEyeButton, PhotoChangeCell, PhotoViewerModal };

const PAGE_SIZE = 50;

export default function HistoryModal({ guid, show, onHide }) {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('fields');
  const [floorMap, setFloorMap] = useState({});
  const [isMultiFloor, setIsMultiFloor] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState(''); // '' = all floors
  const [viewerUrls, setViewerUrls] = useState(null);

  const { assetSchema } = useContext(RootContext);
  const assetFieldLabelMap = useMemo(() => buildAssetFieldLabelMap(assetSchema), [assetSchema]);

  useEffect(() => {
    if (!show) {
      // Reset so the next open starts on page 1 with all floors
      setPage(1);
      setSelectedFloor('');
      return;
    }
    setLoading(true);
    axios.get(`/space-profiles/${guid}/history`, { params: { page, limit: PAGE_SIZE } })
      .then(res => {
        const rows = res.data.records || [];
        setRecords(rows);
        setTotal(Number.isFinite(res.data.total) ? res.data.total : rows.length);
        setFloorMap(res.data.floor_map || {});
        setIsMultiFloor(res.data.is_multi_floor || false);
      })
      .catch(() => {
        setRecords([]);
        setTotal(0);
        setFloorMap({});
        setIsMultiFloor(false);
      })
      .finally(() => setLoading(false));
  }, [show, guid, page]);

  // Annotate each record with its floor_label for display and sorting
  const annotatedRecords = useMemo(() => {
    if (!isMultiFloor || !floorMap || Object.keys(floorMap).length === 0) return records;
    return records.map(r => ({
      ...r,
      floor_label: floorMap[r.space_profile_guid]?.floor_label || 'Level 1'
    }));
  }, [records, floorMap, isMultiFloor]);

  // Apply floor filter when a specific floor is selected
  const floorFilteredRecords = useMemo(() => {
    if (!selectedFloor || !isMultiFloor) return annotatedRecords;
    return annotatedRecords.filter(r => r.space_profile_guid === selectedFloor);
  }, [annotatedRecords, selectedFloor, isMultiFloor]);

  // basic_info / status rows: FIELD_UPDATE and PHOTO_UPDATE with no asset_guid, plus STATUS_CHANGE
  const fieldRecords = useMemo(
    () => floorFilteredRecords.filter(r =>
      ((r.action === 'FIELD_UPDATE' || r.action === 'PHOTO_UPDATE') && !r.asset_guid) ||
      r.action === 'STATUS_CHANGE'
    ),
    [floorFilteredRecords]
  );

  // ASSET_* rows plus field/photo updates that belong to a specific asset
  const assetRecords = useMemo(
    () => floorFilteredRecords.filter(r =>
      r.action === 'ASSET_ADDED' ||
      r.action === 'ASSET_UPDATED' ||
      r.action === 'ASSET_DELETED' ||
      ((r.action === 'FIELD_UPDATE' || r.action === 'PHOTO_UPDATE') && !!r.asset_guid)
    ),
    [floorFilteredRecords]
  );

  // Floor dropdown options sorted by floor number
  const floorOptions = useMemo(() =>
    Object.entries(floorMap)
      .map(([floorGuid, info]) => ({ value: floorGuid, label: info.floor_label || 'Level 1', floor: info.floor || 1 }))
      .sort((a, b) => a.floor - b.floor),
    [floorMap]
  );

  const sharedFloorProps = { isMultiFloor, floorOptions, selectedFloor, onFloorChange: setSelectedFloor };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Sliding window of up to 5 page numbers centered on the current page
  const pageNumbers = useMemo(() => {
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    const end = Math.min(totalPages, start + 4);
    const nums = [];
    for (let n = start; n <= end; n++) nums.push(n);
    return nums;
  }, [page, totalPages]);

  const footer = (
    <div className="d-flex align-items-center w-100">
      <small className="text-muted mr-auto">
        {!loading && 'History is only tracked for changes made on or after April 10, 2026.'}
      </small>
      <Button onClick={onHide} style={{ backgroundColor: '#4068cf', color: 'white', border: 'none', fontSize: 14, borderRadius: 4, padding: '12px 24px', marginLeft: 10 }}>Close</Button>
    </div>
  );

  return (
    <AdminModal
      title="Profile History"
      show={show}
      onHide={onHide}
      onSave={() => { }}
      noSave={true}
      dialogClassName="modal-lg"
      footer={footer}
    >
      {loading ? (
        <div className="d-flex justify-content-center p-4">
          <Spinner animation="border" />
        </div>
      ) : (
        <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-0">
          <Tab eventKey="fields" title={`Space Data (${fieldRecords.length})`}>
            <FieldChangesTab
              records={fieldRecords}
              onViewPhotos={setViewerUrls}
              {...sharedFloorProps}
            />
          </Tab>
          <Tab eventKey="assets" title={`Asset Changes (${assetRecords.length})`}>
            <AssetChangesTab
              records={assetRecords}
              onViewPhotos={setViewerUrls}
              assetFieldLabelMap={assetFieldLabelMap}
              {...sharedFloorProps}
            />
          </Tab>
        </Tabs>
      )}
      {!loading && totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} records
          </small>
          <Pagination size="sm" className="mb-0">
            <Pagination.First disabled={page === 1} onClick={() => setPage(1)} />
            <Pagination.Prev disabled={page === 1} onClick={() => setPage(p => p - 1)} />
            {pageNumbers.map(n => (
              <Pagination.Item key={n} active={n === page} onClick={() => setPage(n)}>{n}</Pagination.Item>
            ))}
            <Pagination.Next disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
            <Pagination.Last disabled={page === totalPages} onClick={() => setPage(totalPages)} />
          </Pagination>
        </div>
      )}
      {viewerUrls && (
        <PhotoViewerModal urls={viewerUrls} onClose={() => setViewerUrls(null)} />
      )}
    </AdminModal>
  );
}
